import { format, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import { isEmptyCell, parseMaintenanceDate } from './helpers';

/** Calendar date as dd/MM/yyyy for exports (uses same parsing as the app UI). */
export function formatDateForCsv(date, pattern = 'dd/MM/yyyy') {
  if (isEmptyCell(date)) return '';
  const dateObj = parseMaintenanceDate(date);
  if (!dateObj || !isValid(dateObj)) return '';
  return format(dateObj, pattern, { locale: fr });
}

function escapeCsvField(value) {
  const str = value == null ? '' : String(value);
  if (str.startsWith('=') || /[",\n\r\t]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * @param {string} fileName
 * @param {string[]} headers
 * @param {string[][]} rows
 */
export function downloadCsv(fileName, headers, rows) {
  const lines = [
    headers.map(escapeCsvField).join(','),
    ...rows.map((row) => row.map(escapeCsvField).join(',')),
  ];
  const blob = new Blob(['\uFEFF' + lines.join('\r\n')], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName.endsWith('.csv') ? fileName : `${fileName}.csv`;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Excel .xlsx export — dates stay visible text (no #### from CSV import quirks).
 * @param {number[]} [textColumnIndexes] — column indexes forced to text (@) format
 */
export function downloadXlsx(fileName, headers, rows, { textColumnIndexes = [] } = {}) {
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

  if (ws['!ref']) {
    const range = XLSX.utils.decode_range(ws['!ref']);
    const textCols = new Set(textColumnIndexes);
    for (let r = range.s.r + 1; r <= range.e.r; r += 1) {
      for (const c of textCols) {
        const addr = XLSX.utils.encode_cell({ r, c });
        const cell = ws[addr];
        if (!cell || cell.v == null || cell.v === '') continue;
        cell.t = 's';
        cell.v = String(cell.v);
        cell.z = '@';
      }
    }
  }

  ws['!cols'] = headers.map((header, index) => ({
    wch: textColumnIndexes.includes(index)
      ? 14
      : Math.min(40, Math.max(10, String(header).length + 4)),
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Export');
  const outName = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  XLSX.writeFile(wb, outName);
}

export function safeExportFileName(label) {
  return String(label || 'export')
    .trim()
    .replace(/[^\w\-àâäéèêëïîôùûüç\s]/gi, '')
    .replace(/\s+/g, '_')
    .slice(0, 80);
}
